import React, { useState } from "react"
import { validation } from "@data/utility/validation"
import { FormStepper } from "@common/components/stepper/FormStepper"
import { View } from "react-native"
import { useNavigation } from "@react-navigation/native"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { connect, useSelector } from "react-redux"
import { plr, whatIsIt } from "@data/utility/Utils"
import moment from "moment/moment"
import { liveService } from "@data/services/liveService"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { congratsTypes } from "@common/screens/CongratsScreen"
import { putCongrats } from "@data/redux/actions/navigation"
import { ActivityIndicator } from "react-native-paper"
import { theme } from "@common/theme/theme"
import RecoverLiveSessionComponent from "./RecoverLiveSessionComponent"
import TextM from "@common/components/text/TextM"

const RecoverFormComponent = (props) => {
    const { withStudent = null } = props.route.params
    const [formValues, setFormValues] = useState()
    const navigation = useNavigation()
    const defaultValues = {}

    const asParent =
        whatIsIt(withStudent) === "String"
            ? withStudent === "true"
            : withStudent
    const user = !asParent ? props.user : props.formDataUser

    const attendanceFlagsToBeRecovered = useSelector(
        (state) => state.data.liveSessions?.attendance_flags_to_be_recovered
    )
    const attendanceFlagsRecoverable = attendanceFlagsToBeRecovered?.filter(
        (flag) => flag?.is_recoverable_now
    )
    const hasOnlyOneRecoverableFlag = attendanceFlagsRecoverable?.length === 1
    const singleRecoverableFlagId = hasOnlyOneRecoverableFlag
        ? attendanceFlagsRecoverable[0]?.id
        : null

    const attendanceFlagsToBeRecoveredOptions =
        attendanceFlagsToBeRecovered?.map((flag) => {
            const durationDays = flag?.recovery_duration_days || 17
            const isParentCreated = !flag?.created_by
            const baseDate =
                isParentCreated && flag?.date
                    ? moment(flag.date)
                    : moment(flag?.created_at)
            const expirationDate = flag?.expiration_date
                ? moment(flag.expiration_date)
                : baseDate.clone().add(durationDays, "days")
            const expiresIn = expirationDate.diff(moment(), "days")
            const expiresSoon = expiresIn <= 7 && expiresIn >= 0

            const descriptionParts = []
            if (flag?.description) {
                descriptionParts.push(flag.description)
            }

            return {
                value: flag?.id,
                disabled: !flag?.is_recoverable_now,
                label: flag?.date
                    ? "Absence du " +
                      moment(flag.date).format("dddd DD MMMM [à] HH:mm")
                    : "Crédit de rattrapage",
                description: flag?.is_recoverable_now ? (
                    <TextM
                        wrap
                        color={
                            expiresSoon ? theme.colors.error : theme.colors.grey
                        }
                    >
                        {descriptionParts.length > 0
                            ? descriptionParts.join(" - ") + " | "
                            : ""}
                        {flag?.date ? "Rattrapage" : "Séance"} à planifier avant
                        le {expirationDate.format("DD MMMM")}
                        {expiresSoon
                            ? ` (${expiresIn} ${plr("jour", expiresIn)})`
                            : ""}
                    </TextM>
                ) : (
                    "Ce rattrapage n'est plus disponible"
                ),
            }
        })

    const selectedAttendanceFlag = attendanceFlagsToBeRecovered?.find(
        (flag) =>
            flag?.id ===
            (formValues?.attendance_flag_id || singleRecoverableFlagId)
    )

    const formData = [
        {
            label: "Planifier mes rattrapages",
            description:
                attendanceFlagsRecoverable?.length === 0
                    ? "Aucun rattrapage disponible pour le moment. Une notification sera envoyée lorsqu'un rattrapage sera possible."
                    : null,
            name: "attendance_flag_id",
            type: "select.single",
            options: attendanceFlagsToBeRecoveredOptions,
            defaultValue: singleRecoverableFlagId ?? null,
            is_masked: hasOnlyOneRecoverableFlag,
            validationRules: { ...validation.required() },
        },
        {
            label: null,
            description: null,
            type: "custom",
            name: "recovery_occurrence_id",
            component: RecoverLiveSessionComponent,
            componentProps: {
                asParent,
                student: user,
                liveSessionId: selectedAttendanceFlag?.live_session_id,
                occurrenceDate: selectedAttendanceFlag?.date,
                attendanceFlagId: selectedAttendanceFlag?.id,
            },
        },
    ]

    const onSubmit = (data) => {
        if (
            props.recoverAbsenceAsStudentRequest ||
            props.recoverAbsenceAsParentRequest
        )
            return
        if (!asParent)
            props.apiRequest(
                liveService.recoverAbsenceAsStudent,
                { id: selectedAttendanceFlag?.id },
                data
            )
        else
            props.apiRequest(
                liveService.recoverAbsenceAsParent,
                { id: selectedAttendanceFlag?.id },
                {
                    ...data,
                    student_email: props.formDataUser?.email,
                }
            )
    }

    const getRecoveryService = !asParent
        ? liveService.getRecoveryPossibilities
        : liveService.getRecoveryPossibilitiesAsParent
    const getRecoveryParams = !asParent
        ? {
              attendance_flag_id: selectedAttendanceFlag?.id,
          }
        : {
              student_email: user?.email,
              attendance_flag_id: selectedAttendanceFlag?.id,
          }

    const getService = !asParent
        ? liveService.getAllMyNextLiveSessions
        : liveService.getAllMyNextLiveSessionsAsParent
    const params = !asParent ? {} : { student_email: user?.email }

    useApiStatus(
        !asParent
            ? liveService.recoverAbsenceAsStudent
            : liveService.recoverAbsenceAsParent, // api service
        null, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        (successData) => {
            props.apiRequest(getService, {}, {}, params)

            const congrats = [
                {
                    congratsType: congratsTypes.GENERIC,
                    congratsData: {
                        title: "Effectué",
                        text: `Le rattrapage a bien été planifié`,
                    },
                },
            ]

            props.putCongrats(congrats)
            navigation.replace("congrats")
        },
        (errorData) => {
            props.apiRequest(getRecoveryService, {}, {}, getRecoveryParams)
        }
    )

    return (
        <View
            style={{
                flex: 1,
                alignItems: "center",
            }}
        >
            {props.getAllMyNextLiveSessionsRequest ||
            props.getAllMyNextLiveSessionsAsParentRequest ? (
                <ActivityIndicator
                    animating={true}
                    style={{
                        marginTop: 50,
                    }}
                    size="small"
                    color={theme.colors.primary}
                />
            ) : (
                <FormStepper
                    navigation={navigation}
                    track
                    formData={formData?.filter((x) => !x.is_masked)}
                    champion={null}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    defaultValues={defaultValues}
                    onSubmit={onSubmit}
                    submitted={
                        props.recoverAbsenceAsStudentRequest ||
                        props.recoverAbsenceAsParentRequest
                    }
                    previousRouteName={"menu"}
                    submittedMessage=""
                    submitButtonText={`Signaler ${formValues?.type === "late" ? "le retard" : "l'absence"}`}
                    serverError={
                        props.recoverAbsenceAsStudentError ||
                        props.recoverAbsenceAsParentError
                    }
                />
            )}
        </View>
    )
}
const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        formDataUser: state.data.formData?.user,
        getUserRequest: state.api.getUser?.request,
        getAllMyNextLiveSessionsRequest:
            state.api.getAllMyNextLiveSessions?.request,
        getAllMyNextLiveSessionsAsParentRequest:
            state.api.getAllMyNextLiveSessionsAsParent?.request,
        recoverAbsenceAsStudentRequest:
            state.api.recoverAbsenceAsStudent?.request,
        recoverAbsenceAsStudentError: state.api.recoverAbsenceAsStudent?.error,
        recoverAbsenceAsParentRequest:
            state.api.recoverAbsenceAsParent?.request,
        recoverAbsenceAsParentError: state.api.recoverAbsenceAsParent?.error,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    putCongrats,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(RecoverFormComponent)
